import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Anchor,
  Banner,
  Box,
  Button,
  ButtonGroup,
  Card,
  CloseButton,
  Heading,
  Icon,
  Tag,
  Text,
  TextButton,
} from '@zillow/constellation-10';

import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import * as S from './PaymentReportingCard.styles';
import * as rentReportingConstants from '@renter-hub/constants/rentReportingConstants';
import type { PaymentReportingInterface } from '@renter-hub/models/Types';
import paths from '@renter-hub/pathsManifest';
import { isTokenValid } from '../../../../utils/creditTrackingUtils';
import { IconCreditScoreFilled } from '@zillow/constellation-icons';

const rentReportingPath = `${paths.baseUrl}${paths.rentReportingPage}`;

type Props = {
  goToPaymentReportingPage: () => void;
  goToRentReportingPage: () => void;
  paymentReportingStatus?: PaymentReportingInterface;
  rentTermsKey: any;
  user: any;
  listingAlias: string;
  onOptOutClick: () => void;
  optOutError: boolean;
  setCurrentTab?: (tabIndex: number) => void;
  fetchCreditTrackingStatus: (rentTermsKey: string) => void;
  enableCreditTracking: boolean;
};

export const PaymentReportingCard = (props: Props) => {
  const { optedIn, optInDate, creditTracking } = props.paymentReportingStatus || { optedIn: false, optInDate: '' };
  const hasAttemptedRefetch = useRef(false);

  const { user, listingAlias, enableCreditTracking } = props;

  useEffect(() => {
    const isTokenInvalid = creditTracking && !isTokenValid(creditTracking);

    if (optedIn && isTokenInvalid && props.rentTermsKey && !hasAttemptedRefetch.current) {
      hasAttemptedRefetch.current = true;
      props.fetchCreditTrackingStatus(props.rentTermsKey);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [optedIn, props.rentTermsKey, creditTracking?.lastFetchedTimestamp]);

  const isEnrolledInCreditTracking = creditTracking?.arrayUserToken && isTokenValid(creditTracking);
  const isUnenrolledInCreditTracking =
    !creditTracking?.arrayUserToken && creditTracking?.enrollmentStatus === 'OPTED_OUT';
  const isNotEnrolledInCreditTracking =
    !creditTracking?.arrayUserToken &&
    (!creditTracking?.enrollmentStatus || creditTracking?.enrollmentStatus === 'NOT_ENROLLED');

  const title = optedIn ? `You’re building credit` : `Want to build credit?`;
  const getSubTitle = () => {
    // opted in for rent reporting, not credit tracking
    if (optedIn) {
      const optInDateAsDate = new Date(optInDate);
      const month = optInDateAsDate?.toLocaleString('default', { month: 'long' });
      const year = optInDateAsDate?.getFullYear();
      return (
        <Text>
          You’ve been enrolled in rent reporting since{' '}
          <strong>
            {month} {year}
          </strong>
          .
        </Text>
      );
    }
    return (
      <Text>Send your on-time rent payment history to Experian, Equifax, and TransUnion to build your credit</Text>
    );
  };

  const getCardContent = () => (
    <Box>
      <S.CardHeader>
        <S.CardTitle>
          <Heading className="pvxs" textStyle="heading-xs" level={3}>
            {title}
          </Heading>
          {!optedIn && <Tag tone="aqua">Free</Tag>}
        </S.CardTitle>
        {getSubTitle()}
      </S.CardHeader>
    </Box>
  );

  const getStartedClick = () => {
    RachelCSEvents.emitOptIntoRentReportingDashboardEvent({ user, currentProperty: { listingAlias } });
    props.goToPaymentReportingPage();
    if (props.setCurrentTab) {
      props.setCurrentTab(1);
    }
  };

  const startTrackingCreditClick = () => {
    RachelCSEvents.emitStartCreditTrackingFromOverviewEvent();
    props.goToRentReportingPage();
    if (props.setCurrentTab) {
      props.setCurrentTab(1);
    }
  };

  const onViewMoreFAQClick = () => {
    RachelCSEvents.emitOpenRentReportingFAQDashboardEvent({ user, currentProperty: { listingAlias } });
  };

  const handleClickToReportingTab = () => {
    if (props.setCurrentTab) {
      props.setCurrentTab(1);
    }
  };

  const renderContent = () => {
    if (!optedIn) {
      return (
        <Box
          css={{
            display: 'flex',
            gap: 'default',
            alignItems: 'flex-start',
            gridColumn: '1 / -1',
          }}
        >
          <Icon size="lg" css={{ flexShrink: 0, color: 'icon.accent.blue.hero' }}>
            <IconCreditScoreFilled />
          </Icon>
          <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {getCardContent()}
            <Button onClick={getStartedClick}>Get started</Button>
          </Box>
        </Box>
      );
    }
    if (optedIn && !enableCreditTracking) {
      return (
        <>
          {getCardContent()}
          <ButtonGroup aria-label="Payment reporting options" orientation="vertical">
            <Button onClick={onViewMoreFAQClick} fluid={true} asChild>
              <Anchor href={rentReportingConstants.CREDIT_REPORTING_FAQ_URL} target="_blank">
                View FAQs
              </Anchor>
            </Button>

            <TextButton textStyle="body-sm" onClick={props.onOptOutClick}>
              Opt out
            </TextButton>
          </ButtonGroup>
        </>
      );
    }

    if (optedIn && isNotEnrolledInCreditTracking) {
      return (
        <Box>
          <Box css={{ display: 'flex', alignItems: 'center', marginBottom: 'default' }}>
            <Heading level={5}>Rent reporting</Heading>
          </Box>
          <Box css={{ display: 'flex', alignItems: 'flex-start', marginBottom: 'tighter' }}>
            <Text css={{ flex: 1 }}>You can now track your credit score directly on your payments dashboard</Text>
          </Box>
          <Box css={{ marginTop: 'default' }}>
            <TextButton onClick={startTrackingCreditClick} textStyle="body-bold" tone="brand">
              Start tracking your credit today
              <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 12 }}>
                <Tag tone="aqua" size="sm" css={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                  New
                </Tag>
              </span>
            </TextButton>
          </Box>
        </Box>
      );
    }

    if (optedIn && (!creditTracking || isUnenrolledInCreditTracking)) {
      // Fallback: show isUnenrolled view if we can't fetch credit tracking status or user is unenrolled
      return (
        <Box>
          <Box css={{ display: 'flex', alignItems: 'center', marginBottom: 'default' }}>
            <Heading level={5}>Rent reporting</Heading>
          </Box>
          {getSubTitle()}
          <Box css={{ marginTop: 'default' }}>
            <TextButton textStyle="body-bold" tone="brand" asChild>
              <Link to={rentReportingPath} onClick={handleClickToReportingTab}>
                See more
              </Link>
            </TextButton>
          </Box>
        </Box>
      );
    }

    if (optedIn && isEnrolledInCreditTracking) {
      return (
        <Box>
          <Box css={{ display: 'flex', alignItems: 'center', marginBottom: 'default' }}>
            <Heading level={5}>Rent reporting</Heading>
          </Box>
          {getSubTitle()}
          <Box css={{ marginTop: 'default' }}>
            <TextButton textStyle="body-bold" tone="brand" asChild>
              <Link to={rentReportingPath} onClick={handleClickToReportingTab}>
                See your latest credit score
              </Link>
            </TextButton>
          </Box>
        </Box>
      );
    }
  };

  return (
    <Card
      css={{
        flex: 1,
        padding: 'default',
      }}
      elevated={false}
    >
      {props.optOutError && (
        <Banner tone="warning" closeButton={<CloseButton onImpact />} css={{ marginBottom: 'default' }}>
          There was an issue opting out of rent payment reporting. Please try again.
        </Banner>
      )}
      <Box
        css={{
          display: enableCreditTracking ? 'flex' : 'grid',
          gridTemplateColumns: { base: '1fr', md: '7fr 5fr' },
          gap: 'default',
        }}
      >
        {renderContent()}
      </Box>
    </Card>
  );
};
